import { FormInstance } from 'antd';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { PaymentInvoiceItem } from '@/lib/types/payment';
import {
  buildValidationPaths,
  extractChangedInvoiceIndexes,
  FieldPath,
  getValidationIndexes,
  getSelectedIndexes,
  hasIbanFieldChanged,
  PAYMENT_ERROR_MAPPINGS,
} from '../_helpers/inPaymentPageHelper';

type UseInPaymentPageValidationParams = {
  form: FormInstance;
  t: TFunction;
  checkedList: number[];
};

type ValidationOptions = {
  invoiceIndexes?: number[];
  includeIban?: boolean;
};

export const useInPaymentPageValidation = ({
  form,
  t,
  checkedList,
}: UseInPaymentPageValidationParams) => {
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [showErrorMessages, setShowErrorMessages] = useState(false);

  const setFieldErrors = useCallback(
    (
      fieldErrors: Array<{
        name: FieldPath;
        errors: string[];
      }>,
      formInvoices: PaymentInvoiceItem[],
      includeIban: boolean,
      selectedIndexes: number[],
    ) => {
      const relevantPaths = buildValidationPaths(
        selectedIndexes,
        includeIban,
        formInvoices,
      );

      form.setFields([
        ...relevantPaths.map((name) => ({ name, errors: [] })),
        ...fieldErrors,
      ]);
    },
    [form],
  );

  const mapValidationKeysToMessages = useCallback(
    (validationKeys: Array<keyof typeof PAYMENT_ERROR_MAPPINGS>) => {
      const nextMessages = [...new Set(validationKeys)]
        .map((fieldName) => PAYMENT_ERROR_MAPPINGS[fieldName])
        .filter(Boolean)
        .map((translationKey) => t(translationKey, { ns: 'errors' }));

      setErrorMessages(nextMessages);
    },
    [t],
  );

  const getSelectedInvoiceIndexesForForm = useCallback(
    (formInvoices: PaymentInvoiceItem[] = []) => {
      return getSelectedIndexes(formInvoices, checkedList);
    },
    [checkedList],
  );

  const checkErrors = useCallback(
    async (options?: ValidationOptions) => {
      const formInvoices =
        (form.getFieldValue('invoices') as PaymentInvoiceItem[] | undefined) ??
        [];
      const selectedIndexes = getSelectedInvoiceIndexesForForm(formInvoices);

      if (!selectedIndexes.length) {
        form.setFields([{ name: ['iban'], errors: [] }]);
        setErrorMessages([]);
        return true;
      }

      const includeIban = options?.includeIban ?? true;
      const validationIndexes = getValidationIndexes(
        selectedIndexes,
        options?.invoiceIndexes,
      );

      const collectValidationResult = (
        indexes: number[],
        includeIbanInResult: boolean,
      ) => {
        const validationKeys: Array<keyof typeof PAYMENT_ERROR_MAPPINGS> = [];
        const fieldErrors: Array<{ name: FieldPath; errors: string[] }> = [];

        if (includeIbanInResult && !form.getFieldValue('iban')) {
          validationKeys.push('iban');
          fieldErrors.push({ name: ['iban'], errors: ['Verplicht'] });
        }

        indexes.forEach((index) => {
          const invoice = formInvoices[index];
          if (!invoice) {
            return;
          }

          if (
            typeof invoice.toPay !== 'number' ||
            invoice.toPay > invoice.invoice.amount
          ) {
            validationKeys.push('toPay');
            fieldErrors.push({
              name: ['invoices', index, 'toPay'],
              errors: [t('payment.toPayMessage')],
            });
          }

          (invoice.partialPayments ?? []).forEach(
            (partialPayment, paymentIndex) => {
              if (
                !partialPayment?.bankAccount?.iban ||
                !partialPayment.bankAccount?.bic
              ) {
                validationKeys.push('bankAccount');
                fieldErrors.push({
                  name: [
                    'invoices',
                    index,
                    'partialPayments',
                    paymentIndex,
                    'bankAccount',
                  ],
                  errors: ['Verplicht'],
                });
              }

              if (!partialPayment?.date) {
                validationKeys.push('date');
                fieldErrors.push({
                  name: [
                    'invoices',
                    index,
                    'partialPayments',
                    paymentIndex,
                    'date',
                  ],
                  errors: ['Verplicht'],
                });
              }

              if (
                typeof partialPayment?.value !== 'number' ||
                partialPayment.value < 0.01
              ) {
                validationKeys.push('value');
                fieldErrors.push({
                  name: [
                    'invoices',
                    index,
                    'partialPayments',
                    paymentIndex,
                    'value',
                  ],
                  errors: ['Minimaal €0,01'],
                });
              }
            },
          );
        });

        return { validationKeys, fieldErrors };
      };

      const allSelectedValidationResult = collectValidationResult(
        selectedIndexes,
        true,
      );
      const targetedValidationResult = collectValidationResult(
        validationIndexes,
        includeIban,
      );

      setFieldErrors(
        targetedValidationResult.fieldErrors,
        formInvoices,
        includeIban,
        validationIndexes,
      );
      mapValidationKeysToMessages(allSelectedValidationResult.validationKeys);
      return allSelectedValidationResult.validationKeys.length === 0;
    },
    [
      form,
      getSelectedInvoiceIndexesForForm,
      mapValidationKeysToMessages,
      setFieldErrors,
      t,
    ],
  );

  useEffect(() => {
    if (showErrorMessages) {
      void checkErrors();
    }
  }, [checkErrors, checkedList, showErrorMessages]);

  const handleFieldsChange = useCallback(
    (changedFields: Array<{ name: Array<string | number> }>) => {
      if (!showErrorMessages) {
        return;
      }

      const changedInvoiceIndexes = extractChangedInvoiceIndexes(changedFields);
      const includeIban = hasIbanFieldChanged(changedFields);

      if (!changedInvoiceIndexes.length && !includeIban) {
        return;
      }

      void checkErrors({
        invoiceIndexes: changedInvoiceIndexes,
        includeIban,
      });
    },
    [checkErrors, showErrorMessages],
  );

  return {
    errorMessages,
    showErrorMessages,
    setShowErrorMessages,
    checkErrors,
    handleFieldsChange,
  };
};
